import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, BookOpen, ExternalLink } from 'lucide-react';

/**
 * SecurityReviewStatus — bottom card of the Privacy tab.
 *
 * Shows the current status of the external penetration test track:
 *   • "Scheduled"           — before we've kicked off
 *   • "In progress"         — while a firm is engaged
 *   • "Independently penetration-tested by [FIRM] — [MONTH YYYY]"
 *                             — flips once complete
 *
 * Values live in one place (this file) so a future engineer only has
 * to edit these constants to move the badge forward.
 */
const REVIEW_STATE = 'scheduled'; // 'scheduled' | 'in_progress' | 'complete'
const REVIEW_MONTH = 'Q4 2026';   // used when scheduled / in_progress
const REVIEW_FIRM = '';           // populated once complete
const REVIEW_COMPLETED_DATE = ''; // e.g. "March 2026"

const SecurityReviewStatus = () => {
  let badgeText;
  let badgeClass;
  let body;
  if (REVIEW_STATE === 'complete') {
    badgeText = 'Passed';
    badgeClass = 'bg-emerald-100 text-emerald-700 border-emerald-200';
    body = `Independently penetration-tested by ${REVIEW_FIRM} — ${REVIEW_COMPLETED_DATE}.`;
  } else if (REVIEW_STATE === 'in_progress') {
    badgeText = 'In progress';
    badgeClass = 'bg-amber-100 text-amber-700 border-amber-200';
    body = `An independent security firm is currently testing MyInvestFolio. Expected completion: ${REVIEW_MONTH}.`;
  } else {
    badgeText = 'Scheduled';
    badgeClass = 'bg-slate-100 text-slate-600 border-slate-200';
    body = `We have commissioned an independent security firm to penetration-test MyInvestFolio. Expected: ${REVIEW_MONTH}.`;
  }

  return (
    <Card data-testid="security-review-status" className="border-slate-200">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center border border-slate-200 flex-shrink-0">
              <ShieldCheck className="w-5 h-5 text-slate-700" aria-hidden="true" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Independent Security Review</p>
              <p className="text-sm text-slate-700 mt-1 leading-relaxed max-w-xl">{body}</p>
            </div>
          </div>
          <Badge data-testid="security-review-badge" className={`px-2.5 py-1 text-xs font-semibold border ${badgeClass}`}>
            {badgeText}
          </Badge>
        </div>
        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-4 flex-wrap">
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="privacy-policy-link"
            className="inline-flex items-center gap-1.5 text-sm text-emerald-700 hover:text-emerald-900 font-medium"
          >
            <BookOpen className="w-3.5 h-3.5" />
            Read our Privacy Policy in plain English
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </CardContent>
    </Card>
  );
};

export default SecurityReviewStatus;
